import {
  FaBalanceScale,
  FaClipboardCheck,
  FaNetworkWired,
} from "react-icons/fa";
import { FaDatabase } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { IoShieldCheckmark } from "react-icons/io5";
import {
  LuBrainCircuit,
  LuFileCheck,
  LuFileJson,
  LuFileSpreadsheet,
} from "react-icons/lu";

export const effieSystem = {
  orchestrator: {
    title: "Master Orchestrator",
    subtitle: "The State Machine Brain",
    icon: <LuBrainCircuit size={24} />,
    color: "text-rose-400",
    bg: "bg-rose-500/10",
    border: "border-rose-500",
    description:
      "Effie is not just a chatbot; she is a State Machine. She breaks high-level goals ('Build Report') into atomic sub-tasks.",
    details: [
      "Updates 'World State' in real-time",
      "Delegates to Research & Coding Agents",
      "Drives the pulsing UI animations",
    ],
    visual: "brain",
  },
  mcp: {
    title: "MCP Hub",
    subtitle: "Universal Plug-and-Play",
    icon: <FaNetworkWired size={24} />,
    color: "text-cyan-400",
    bg: "bg-cyan-500/10",
    border: "border-cyan-500",
    description:
      "Instead of hard-coded APIs, Effie uses the Model Context Protocol to 'hot-swap' tools like GitHub, Drive, or Notion.",
    details: [
      "Standardized 'Socket' Architecture",
      "Dynamic RAG Chunking Control",
      "Zero-Code Integration Swapping",
    ],
    visual: "hub",
  },
  critic: {
    title: "Critic Loop",
    subtitle: "Autonomous Self-Correction",
    icon: <FiRefreshCw size={24} />,
    color: "text-purple-400",
    bg: "bg-purple-500/10",
    border: "border-purple-500",
    description:
      "Trust is built through verification. The Critic Agent reviews output (code/text) and rejects errors before you see them.",
    details: [
      "Automated Code Review",
      "Hallucination Checks",
      "Real-time Retry Loops",
    ],
    visual: "loop",
  },
  sentinel: {
    title: "IP Sentinel",
    subtitle: "The Trust Fortress",
    icon: <IoShieldCheckmark size={24} />,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500",
    description:
      "The final gatekeeper. A deterministic layer that scans for PII and ensures IP attribution before data leaves the secure zone.",
    details: [
      "PII Redaction (Regex/NER)",
      "IP Hash Verification",
      "Data Leakage Prevention",
    ],
    visual: "shield",
  },
};

export const effieOnBoardingSections = [
  {
    id: "rag-intro",
    title: "How RAG Works",
    subtitle: "The Smart Librarian",
    content: (
      <>
        <p className="mb-4">
          RAG, or Retrieval-Augmented Generative AI, enables computers to
          generate human-like responses by accessing a vast library of
          information.
        </p>
        <p className="mb-4">
          Initially, RAG stores a collection of question-answer pairs in its
          memory, similar to books in a library. When you pose a question, the
          system searches these pairs for the most relevant one.
        </p>
        <p>
          It doesn't just parrot the answer; it acts like a smart librarian,
          blending the retrieved facts with generative capabilities to explain
          the content in a helpful, natural way.
        </p>
      </>
    ),
    visual: "librarian",
  },
  {
    id: "training",
    title: "Training Effie",
    subtitle: "Scenarios & Gathering Data",
    content: (
      <>
        <p className="mb-4">
          <strong className="text-[#45A29E]">Getting Started:</strong> The
          onboarding process begins with enhancing the QA pairs that serve as
          Effie's foundation.
        </p>
        <p className="mb-4">
          <strong className="text-[#45A29E]">Exploring Scenarios:</strong>{" "}
          Interact with Effie across various scenarios (Supply Chain, Logistics)
          and diverse age groups. Ask broad questions related to your business.
        </p>
        <p>
          As you do this, Effie captures and logs these pairs. We continue this
          until a significant number of pairs are collected, ensuring she is
          well-versed in diverse inquiries.
        </p>
      </>
    ),
    visual: "chat",
  },
  {
    id: "input",
    title: "Injecting Knowledge",
    subtitle: "Excel & JSON Uploads",
    content: (
      <>
        <p className="mb-4">
          Encountered a missing answer? You have full control to manually expand
          the knowledge base.
        </p>
        <ul className="space-y-4 my-6">
          <li className="flex gap-3">
            <div className="mt-1 bg-green-900/30 p-2 rounded text-green-400">
              <LuFileSpreadsheet size={18} />
            </div>
            <div>
              <strong className="text-white">Excel Sheet:</strong> Document
              questions in one column and answers in another. Upload to track
              unanswered queries.
            </div>
          </li>
          <li className="flex gap-3">
            <div className="mt-1 bg-amber-900/30 p-2 rounded text-amber-400">
              <LuFileJson size={18} />
            </div>
            <div>
              <strong className="text-white">JSON Upload:</strong>{" "}
              Alternatively, upload a structured JSON file via the "Company
              Edit" menu for instant ingestion.
            </div>
          </li>
          <li className="flex gap-3">
            <div className="mt-1 bg-green-900/30 p-2 rounded text-green-400">
              <LuFileSpreadsheet size={18} />
            </div>
            <div>
              <strong className="text-white">Scrape URL:</strong> Effie can
              scrape your website, social media profile or any document that
              describes your business.
            </div>
          </li>
        </ul>
      </>
    ),
    visual: "upload",
  },
  {
    id: "eval",
    title: "Evaluation & Refinement",
    subtitle: "Quality Assurance",
    content: (
      <>
        <p className="mb-4">
          <strong className="text-[#45A29E]">Evaluating Effectiveness:</strong>{" "}
          After gathering data, we assess relevance, accuracy, and topic
          coverage to identify areas for improvement.
        </p>
        <p>
          <strong className="text-[#45A29E]">Adjusting Pairs:</strong> Based on
          results, we refine answers, add new questions, or reorganize
          structure. The goal is to create a reliable resource that effectively
          addresses user queries.
        </p>
      </>
    ),
    visual: "scanner",
  },
  {
    id: "iterative",
    title: "Iterative Evolution",
    subtitle: "Continuous Improvement",
    content: (
      <>
        <p className="mb-4">
          The refinement of QA pairs is an ongoing process. As Effie interacts
          with users and gathers more real-world data, these pairs are regularly
          updated.
        </p>
        <p>
          By actively engaging with Effie and providing feedback, you contribute
          to the continuous enhancement of the AI system, maintaining its
          relevance and accuracy over time.
        </p>
      </>
    ),
    visual: "evolution",
  },
];

export const governancePipelineSteps = [
  {
    id: "governance",
    title: "Protocols & Regulations",
    icon: <FaBalanceScale size={28} />,
    color: "text-rose-400",
    borderColor: "border-rose-500",
    bgGlow: "shadow-[0_0_30px_rgba(244,63,94,0.3)]",
    description:
      "The foundation. We load compliance rules before any user action.",
    details: [
      "ISO 27001 Standards",
      "GDPR Constraints",
      "Internal Safety Manual v4",
    ],
    position: "col-start-1",
  },
  {
    id: "intake",
    title: "Staff Intake Forms",
    icon: <FaClipboardCheck size={28} />,
    color: "text-blue-400",
    borderColor: "border-blue-500",
    bgGlow: "shadow-[0_0_30px_rgba(59,130,246,0.3)]",
    description:
      "User input is validated against the active protocols instantly.",
    details: [
      "Incident Report Form",
      "Patient Data Entry",
      "Claims Processing",
    ],
    position: "col-start-2",
  },
  {
    id: "orchestration",
    title: "Agentic Orchestrator",
    icon: <LuBrainCircuit size={28} />,
    color: "text-purple-400",
    borderColor: "border-purple-500",
    bgGlow: "shadow-[0_0_30px_rgba(168,85,247,0.3)]",
    description:
      "Agents are assigned tasks based on the complexity of the intake.",
    details: ["Routing Agent", "Context Windowing", "Task Delegation"],
    position: "col-start-3",
  },
  {
    id: "rag",
    title: "Knowledge & Verification",
    icon: <FaDatabase size={28} />,
    color: "text-emerald-400",
    borderColor: "border-emerald-500",
    bgGlow: "shadow-[0_0_30px_rgba(16,185,129,0.3)]",
    description:
      "Retrieving training data and verifying output against regulations.",
    details: ["Vector Search", "Hallucination Check", "Citation Mapping"],
    position: "col-start-4",
  },
  {
    id: "output",
    title: "Secure Output",
    icon: <LuFileCheck size={28} />,
    color: "text-amber-400",
    borderColor: "border-amber-500",
    bgGlow: "shadow-[0_0_30px_rgba(245,158,11,0.3)]",
    description:
      "Final synthesis delivered in a standardized, compliant format.",
    details: ["PDF Generation", "Audit Log Update", "Encrypted Delivery"],
    position: "col-start-5",
  },
];
